import React, { ComponentType, useCallback, useEffect, useMemo } from "react";

import { defineMessages, useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import Identified from "@mapmycustomers/shared/types/base/Identified";
import { MultiPin } from "@mapmycustomers/shared/types/map";
import RangeModel from "@mapmycustomers/shared/types/viewModel/internalModel/RangeModel";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";

import { EntityItemProperties } from "@app/component/RecordCard";
import {
  locationPaginationTextGetterFactory,
  RecordList,
  RecordListProps,
  recordsCountTextGetterFactory,
  TextGetters,
} from "@app/component/RecordList";
import { mapEntityIdGetter } from "@app/util/map/idGetters";

import MultiPinInformation from "../MultiPinInformation";

type Entity = Identified & { entity: EntityType };

const messages = defineMessages({
  placeholder: {
    id: "recordsList.multiPin.search.placeholder",
    defaultMessage: "Search records at this location",
    description: "Records list for multi-pin - search placeholder",
  },
});

interface MultiPinRecordListProps<T extends Entity> {
  itemComponent: ComponentType<EntityItemProperties<T>>;
  loading?: boolean;
  multiPin?: MultiPin;
  multiPinAddress?: string;
  onFetchRecords: (payload: { request: Partial<MapViewState> }) => void;
  pageSize: number;
  range: RangeModel;
  records: T[];
  recordsCount: number;
}

const MultiPinRecordList = <T extends Entity>({
  itemComponent,
  loading,
  multiPin,
  multiPinAddress,
  onFetchRecords,
  pageSize,
  range,
  records,
  recordsCount,
}: MultiPinRecordListProps<T>) => {
  const intl = useIntl();

  useEffect(() => {
    if (multiPin) {
      onFetchRecords({ request: { range: { endRow: pageSize, startRow: 0 } } });
    }
  }, [onFetchRecords, multiPin, pageSize]);

  const handleRangeChange = useCallback<NonNullable<RecordListProps<T>["onRangeChange"]>>(
    (range) => onFetchRecords({ request: { range } }),
    [onFetchRecords]
  );

  const handleSearch = useCallback<NonNullable<RecordListProps<T>["onSearch"]>>(
    (search) => onFetchRecords({ request: { search } }),
    [onFetchRecords]
  );

  const textGetters: TextGetters = useMemo(
    () => ({
      countTextGetter: recordsCountTextGetterFactory(intl),
      paginationTextGetter: locationPaginationTextGetterFactory(intl),
    }),
    [intl]
  );

  return multiPin ? (
    <>
      <MultiPinInformation multiPinAddress={multiPinAddress} recordsCount={recordsCount} />

      <RecordList<T>
        idGetter={mapEntityIdGetter}
        itemComponent={itemComponent}
        loading={loading}
        onRangeChange={handleRangeChange}
        onSearch={handleSearch}
        pageSize={pageSize}
        range={range}
        records={records}
        searchPlaceholder={intl.formatMessage(messages.placeholder)}
        showCount={false}
        textGetters={textGetters}
        total={recordsCount}
      />
    </>
  ) : null;
};

export default MultiPinRecordList;
